/**
 * Onboarding advance affordance.
 *
 * Replaces the boring Next pill with a single animated chevron that
 * glides right and fades in a gentle loop. Conveys "swipe or tap" in
 * one gesture without shouting. The whole hit area is tappable so the
 * user can advance by tapping the chevron instead of swiping.
 *
 * Fades in once typing completes so it never competes with the text
 * for attention while the text is still arriving.
 */
import { ChevronRight } from "lucide-react";

interface SwipeHintProps {
  ready: boolean;
  onAdvance: () => void;
  label?: string;
}

export function SwipeHint({ ready, onAdvance, label }: SwipeHintProps) {
  return (
    <button
      type="button"
      onClick={ready ? onAdvance : undefined}
      aria-hidden={!ready}
      tabIndex={ready ? 0 : -1}
      className={`group flex items-center gap-3 rounded-full px-6 py-3 transition-opacity duration-700 ${
        ready ? "opacity-100" : "pointer-events-none opacity-0"
      }`}
    >
      {label && (
        <span className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
          {label}
        </span>
      )}
      <span
        className="inline-flex text-foreground/70"
        style={{ animation: "swipe-hint 1.6s ease-in-out infinite" }}
      >
        <ChevronRight className="h-6 w-6" strokeWidth={2.25} />
      </span>
    </button>
  );
}
